import {useEffect, useState} from 'react';

/**
 * useLocalStorage hook - set and read values from browser's localStorage
 * @param key local storage key
 * @param initialValue initial value in case that no value is set in localStorage
 */
const useLocalStorage = <T extends JSONValue>(
    key: string,
    initialValue: T | null = null,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
    const storage = typeof window === 'object' ? localStorage : null;

    const [value, setValue] = useState<T>(() => {
        if (!storage) {
            return '';
        }

        const serializedValue = storage?.getItem(key);

        if (serializedValue === null) {
            return initialValue;
        }

        try {
            return JSON.parse(serializedValue);
        } catch {
            return serializedValue;
        }
    });

    useEffect(() => {
        if (storage) {
            storage.setItem(key, JSON.stringify(value));
        }
    }, [key, value]);

    return [value, setValue];
};

export default useLocalStorage;
